import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AustralianControllingCorporation } from '../types';
import { recordActionAsEnum, recordActionFromEnum } from 'psims/models/api/data-submission-record-action';
import { cleanSubmissionEntityList } from '../../../shared/update-data-utils';
import { isEmpty } from 'psims/lib/empty';
import { UseFocusedField } from 'psims/react/util/use-focused-field';
import { FocusField, FieldValidation } from '../types';
import { validateAustralianControllingCorporation, ACCValidationField, ACCValidationResult } from './aus-control-corps-validations';
import { getACCInlineMessage } from './aus-control-corps-inline-messages';
import { getACCNotificationMessage } from './aus-control-corps-notifications';

let _keyCounter = 0;
const newKey = () => `acc_${++_keyCounter}`;

function stampRecords(
  raw: AustralianControllingCorporation[],
  keyMap: Map<number, string>
): AustralianControllingCorporation[] {
  return (raw || []).map(x => {
    if (x.id != null && !keyMap.has(x.id)) {
      keyMap.set(x.id, newKey());
    }
    const stableKey = x.id != null ? keyMap.get(x.id)! : newKey();
    return {
      ...x,
      _id: Symbol(),
      _key: stableKey,
      recordAction: x.recordAction ?? undefined,
    };
  });
}

export default function useAustralianControllingCorporations(
  initial: AustralianControllingCorporation[],
  dataSubmissionId: number,
  focusFieldCtrl: UseFocusedField<FocusField>,
) {
  const keyMapRef = useRef<Map<number, string>>(new Map());

  const [data, setData] = useState<AustralianControllingCorporation[]>(() =>
    stampRecords(initial, keyMapRef.current)
  );

  const serverIdSignature = useMemo(() =>
    (initial || []).map(x => x.id ?? 'new').join(','),
    [initial]
  );
  const prevSignatureRef = useRef(serverIdSignature);

  useEffect(() => {
    if (serverIdSignature !== prevSignatureRef.current) {
      prevSignatureRef.current = serverIdSignature;
      setData(stampRecords(initial, keyMapRef.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverIdSignature]);

  const addWithFlag = useCallback(() => {
    setData(prev => [...prev, {
      _id: Symbol(),
      _key: newKey(),
      dataSubmissionId,
      australianControllingCorporationName: undefined,
      newCorporationName: undefined,
      abn: undefined,
      acn: undefined,
      address: undefined,
      contactInformation: undefined,
      comments: undefined,
      hasChangedInPreviousYear: true,
      recordAction: recordActionAsEnum('Create'),
    }]);
  }, [dataSubmissionId]);

  const add = useCallback(() => {
    setData(prev => [...prev, {
      _id: Symbol(),
      _key: newKey(),
      dataSubmissionId,
      australianControllingCorporationName: undefined,
      newCorporationName: undefined,
      abn: undefined,
      acn: undefined,
      address: undefined,
      contactInformation: undefined,
      comments: undefined,
      hasChangedInPreviousYear: undefined,
      recordAction: recordActionAsEnum('Create'),
    }]);
  }, [dataSubmissionId]);

  const update = useCallback((updated: AustralianControllingCorporation) => {
    setData(prev => prev.map(r =>
      r._id === updated._id
        ? { ...updated, recordAction: recordActionAsEnum(updated.id ? 'Update' : 'Create') }
        : r
    ));
  }, []);

  const remove = useCallback((row: AustralianControllingCorporation) => {
    setData(prev => {
      const target = prev.find(r => r._id === row._id);
      if (!target) return prev;
      if (target.id != null) {
        return prev.map(r =>
          r._id === row._id ? { ...r, recordAction: recordActionAsEnum('Delete') } : r
        );
      }
      return prev.filter(r => r._id !== row._id);
    });
  }, []);

  const dataView = useMemo(() =>
    data
      .filter(d => recordActionFromEnum(d.recordAction) !== 'Delete')
      .map(row => {
        const validationResults = validateAustralianControllingCorporation(row);

        const validations = validationResults.reduce((map, validationResult) => ({
          ...map,
          [validationResult.key]: {
            inline: getACCInlineMessage(validationResult),
            notification: {
              parts: getACCNotificationMessage(
                validationResult.key,
                () => focusFieldCtrl.setFocusedField({
                  _id: row._id,
                  field: validationResult.key,
                  kind: 'data',
                }),
                validationResult,
              ),
            },
            validationResult,
          },
        }), {} as Record<ACCValidationField, FieldValidation<ACCValidationResult>>);

        return { data: row, validations };
      }),
    [data, focusFieldCtrl]
  );

  const rows = useMemo(() => dataView, [dataView]);

  const anyInvalid = useMemo(
    () => dataView.some(v => !isEmpty(v.validations)),
    [dataView]
  );

  const requestEntities = useMemo(() =>
    cleanSubmissionEntityList(data).map(({ _key, ...rest }) => rest),
    [data]
  );

  return useMemo(() => ({
    rows,
    add,
    addWithFlag,
    update,
    remove,
    requestEntities,
    anyInvalid,
  }), [rows, add, addWithFlag, update, remove, requestEntities, anyInvalid]);
}

export type UseAustralianControllingCorporations = ReturnType<typeof useAustralianControllingCorporations>;